import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { gql } from '@apollo/client';
import { Expandable, Text } from '@moda/om';
import { Availability } from '@moda/portal-stanchions';
import {
  ProductDetailsExpandedFragment,
  ProductDetailsExpandedVariantFragment,
  SizeInventoryFragment
} from '../../generated/types';
import { tc } from '../../lib/trackingContext';
import { addSubjectToMailLinks } from '../../lib/addSubjectToMailLinks';
import { usePageType } from '../../hooks/usePageType';
import { HolidayDelayContainer } from './HolidayDelayContainer/HolidayDelayContainer';
import { ProductDetailsShippingAndReturns } from './ProductDetailsShippingAndReturns';

export const PRODUCT_DETAILS_EXPANDED_FRAGMENT = gql`
  fragment ProductDetailsExpandedFragment on Variant {
    id
    available
    description
    availability
    fitBrand: fit_brand
    productId: product_id
    isBoutique: is_boutique
    detailBullets: detail_bullets
    fulfilmentType: fulfilment_type
    descriptionText: description_text
    masterVariantId: master_variant_id
    shippingRestrictions: shipping_restrictions
    howToUse: how_to_use
    ingredients
    clinicalStudies: clinical_studies
  }
`;

export const PRODUCT_DETAILS_EXPANDED_VARIANT_FRAGMENT = gql`
  fragment ProductDetailsExpandedVariantFragment on VariantData {
    returnPolicy: return_policy
  }
`;

interface Props {
  activeVariant: ProductDetailsExpandedVariantFragment;
  variant: ProductDetailsExpandedFragment;
  size: SizeInventoryFragment | null;
}

export const ProductDetailsExpanded: React.FC<Props> = ({
  size,
  variant,
  activeVariant: { returnPolicy },
  variant: {
    fitBrand,
    productId,
    available,
    availability,
    detailBullets,
    fulfilmentType,
    shippingRestrictions,
    howToUse,
    ingredients,
    clinicalStudies
  }
}) => {
  const { vertical = 'women' } = useParams<{ vertical?: string }>();
  const pagetype = usePageType();

  const trackExpanded = useCallback(() => {
    tc.track('Size & Fit Tab Selected', {
      addToScopeOnce: { variant },
      addToTrackingEvent: { pagetype }
    });
  }, [variant, pagetype]);

  const availableNow = availability === String(Availability.AvailableNow);

  const addSubjectToCareEmailLinks = (html: string | null) =>
    addSubjectToMailLinks({
      html,
      address: 'care@modaoperandi.com',
      subject: `Trunkshow Expertise Request for Product ID ${productId}`
    });

  return (
    <>
      <Expandable
        name={<Text treatment="bold1">PRODUCT DETAILS</Text>}
        defaultExpanded
        data-testid="productDetailsExpandable"
      >
        <div dangerouslySetInnerHTML={{ __html: addSubjectToCareEmailLinks(detailBullets) }} />
        <p>Product Code {productId}</p>
      </Expandable>

      {fitBrand !== '<p></p>\n<p></p>\n' && fitBrand !== '' && (
        <Expandable
          name={<Text treatment="bold1">{vertical === 'home' ? 'DIMENSIONS' : 'SIZE & FIT'}</Text>}
          defaultExpanded
          data-testid="sizeExpandable"
          onClick={trackExpanded}
        >
          <div dangerouslySetInnerHTML={{ __html: addSubjectToCareEmailLinks(fitBrand) }} />
        </Expandable>
      )}

      {howToUse != null && howToUse !== '<p></p>\n' && (
        <Expandable name={<Text treatment="bold1">HOW TO USE</Text>}>
          <div dangerouslySetInnerHTML={{ __html: addSubjectToCareEmailLinks(howToUse) }} />
        </Expandable>
      )}
      {ingredients != null && ingredients !== '<p></p>\n' && (
        <Expandable name={<Text treatment="bold1">INGREDIENTS</Text>}>
          <div dangerouslySetInnerHTML={{ __html: addSubjectToCareEmailLinks(ingredients) }} />
        </Expandable>
      )}
      {clinicalStudies != null && clinicalStudies != '' && clinicalStudies !== '<p></p>\n' && (
        <Expandable name={<Text treatment="bold1">CLINICAL STUDIES</Text>}>
          <div dangerouslySetInnerHTML={{ __html: addSubjectToCareEmailLinks(clinicalStudies) }} />
        </Expandable>
      )}

      <Expandable
        name={<Text treatment="bold1">SHIPPING &amp; RETURNS</Text>}
        defaultExpanded
        data-testid="ShippingExpandable"
      >
        <HolidayDelayContainer />

        <ProductDetailsShippingAndReturns
          size={size}
          available={available}
          returnPolicy={returnPolicy}
          availableNow={availableNow}
          fulfilmentType={fulfilmentType}
          shippingRestrictions={shippingRestrictions}
          variantId={variant.id}
        />
      </Expandable>
    </>
  );
};
